import * as Sentry from '@sentry/react';

import {
  clearSession,
  getTokens,
  isIdTokenExpired,
  setTokens,
  type AuthTokens,
} from './session';

const tokenUrl = () =>
  `${import.meta.env.VITE_COGNITO_DOMAIN.replace(/\/$/, '')}/oauth2/token`;

const clientId = () => import.meta.env.VITE_COGNITO_CLIENT_ID as string;

interface CognitoTokenResponse {
  id_token: string;
  refresh_token?: string;
}

/**
 * How a refresh ended. Only `refused` means the session is over: Cognito
 * looked at the refresh token and said no (expired, revoked, or none held).
 * `network` means Cognito was never reached or did not answer properly
 * (offline, DNS, a 5xx); the refresh token may be perfectly good, so the
 * session is kept and the next call tries again.
 */
export type RefreshResult =
  | { kind: 'ok'; tokens: AuthTokens }
  | { kind: 'refused' }
  | { kind: 'network' };

/**
 * Exchanges the stored refresh token for new tokens at Cognito's OAuth2 token
 * endpoint, and stores them on success. Never throws: this runs before the app
 * renders, and a rejection there used to mean a blank page.
 */
export async function refreshWithCognito(): Promise<RefreshResult> {
  const { refreshToken } = getTokens();
  if (!refreshToken) return { kind: 'refused' };

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: clientId(),
    refresh_token: refreshToken,
  });

  let res: Response;
  try {
    res = await fetch(tokenUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
  } catch (error) {
    Sentry.captureMessage('cognito refresh could not reach cognito', {
      level: 'warning',
      extra: { error: String(error) },
    });
    return { kind: 'network' };
  }

  // Only 400/401 is Cognito's answer about the token (invalid_grant and
  // friends). Anything else, a 429 throttle or a 5xx included, says nothing
  // about it and is treated like an unreachable Cognito.
  if (res.status === 400 || res.status === 401) {
    return { kind: 'refused' };
  }
  if (!res.ok) {
    Sentry.captureMessage('cognito refresh failed without a verdict on the token', {
      level: 'warning',
      extra: { status: res.status },
    });
    return { kind: 'network' };
  }

  try {
    const data = (await res.json()) as CognitoTokenResponse;
    if (!data.id_token) return { kind: 'network' };
    // A sign-in that landed while this was in flight replaced the refresh token;
    // storing this answer would wipe it. Nothing to renew, so report it like an
    // unanswered refresh: the session is kept and the next call tries again.
    if (getTokens().refreshToken !== refreshToken) return { kind: 'network' };
    const tokens: AuthTokens = {
      idToken: data.id_token,
      refreshToken: data.refresh_token ?? refreshToken,
    };
    setTokens(tokens);
    return { kind: 'ok', tokens };
  } catch (error) {
    Sentry.captureMessage('cognito refresh answer could not be read', {
      level: 'warning',
      extra: { error: String(error) },
    });
    return { kind: 'network' };
  }
}

/**
 * Whether the app can start signed in:
 * - `valid`: the id token is fresh, or was just renewed.
 * - `signed_out`: there is no session, or Cognito refused to renew it; the
 *   stored tokens are cleared.
 * - `offline`: renewal could not reach Cognito. The tokens are kept, so the
 *   session survives a page load made while the network was down; API calls
 *   retry the refresh when they get a 401.
 */
export type SessionState = 'valid' | 'signed_out' | 'offline';

export async function ensureFreshTokens(): Promise<SessionState> {
  const { idToken, refreshToken } = getTokens();

  if (idToken && !isIdTokenExpired(idToken)) {
    return 'valid';
  }

  if (!refreshToken) {
    clearSession();
    return 'signed_out';
  }

  const result = await refreshWithCognito();
  if (result.kind === 'ok') return 'valid';
  if (result.kind === 'network') return 'offline';
  clearSession();
  return 'signed_out';
}
